import React from 'react'
import { Button } from 'semantic-ui-react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useAddListHeightOffset } from '@cbgms/components/list-wrapper/ListWrapperUtils/ListHeightOffsetContext'

export interface IFormButtonsProps {
  onDelete?: () => void
  onSubmit?: (event: React.FormEvent) => void // this is a onSubmit, alreaddy wrapped in handleSubmit from the redux-form
  onSubmitAndDone?: (event: React.FormEvent) => void // this is a onSubmit, alreaddy wrapped in handleSubmit from the redux-form
  onCancel?: () => void
  pristine?: boolean
  submitting?: boolean
  fromModal?: boolean
  invalid?: boolean
  asOptionalComponent?: boolean
}

const FormButtonsFC = (props: IFormButtonsProps) => {
  const { t } = useTranslation()

  // Since we place this component above the ListWrapper a lot, use this hook to add the
  // height of this component to the ListWrapper height offset.
  // It's a little bit hacky, but at least for the 90% case developers won't have to think about
  // the ListWrapper height offset.
  const offset = !props.asOptionalComponent ? 31 : 0
  useAddListHeightOffset(offset)

  return <FormButtons {...props} t={t} />
}

/**
 * @deprecated: Prefer to use material-ui buttons when possible.
 * We have `packages\form\src\BackButton.tsx` and `packages\form\src\SubmitButton.tsx` for this now.
 * And there's also `packages\form\src\BasicFormLayout.tsx` which combines the back and submit buttons.
 */
class FormButtons extends React.Component<IFormButtonsProps & { t: TFunction }, {}> {
  state = {
    delete: false,
    submit: false,
    submitAndDone: false
  }

  submit = async (fn: (event: React.FormEvent) => void, key: string, event: React.FormEvent) => {
    this.setState({
      [key]: true
    })

    try {
      const result = await fn(event)

      this.setState({
        [key]: false
      })

      return result
    } catch (error) {
      this.setState({
        [key]: false
      })

      throw error
    }
  }

  render() {
    const { onCancel, onDelete, onSubmit, onSubmitAndDone, pristine, submitting, fromModal, invalid, t } = this.props

    return (
      <div className='form-buttons'>
        {onDelete && (
          <Button
            className={fromModal ? 'form-buttons__delete btn-delete' : 'btn-delete'}
            content={t('global.delete')}
            icon='trash'
            negative
            disabled={submitting}
            onClick={onDelete}
            type='button'
            data-testid='btn-delete'
            size='tiny'
          />
        )}

        {onCancel && (
          <Button
            className='ml-half'
            color='orange'
            content={t('global.back')}
            icon='arrow left'
            disabled={submitting}
            onClick={onCancel}
            type='button'
            data-testid='btn-cancel'
            size='tiny'
          />
        )}

        {onSubmit && (
          <Button
            className='ml-half'
            content={t('global.save')}
            icon='save'
            loading={this.state.submit}
            disabled={invalid || submitting || pristine}
            onClick={e => this.submit(onSubmit, 'submit', e)}
            positive
            type='submit'
            data-testid='btn-submit'
            size='tiny'
          />
        )}
        {onSubmitAndDone && (
          <Button
            className='ml-half'
            content={t('global.done')}
            icon='save'
            loading={this.state.submitAndDone}
            disabled={invalid || submitting || pristine}
            onClick={e => this.submit(onSubmitAndDone, 'submitAndDone', e)}
            positive
            type='button'
            data-testid='btn-done'
            size='tiny'
          />
        )}
      </div>
    )
  }
}

/**
 * @deprecated: Prefer to use material-ui buttons when possible.
 * We have `packages\form\src\BackButton.tsx` and `packages\form\src\SubmitButton.tsx` for this now.
 * And there's also `packages\form\src\BasicFormLayout.tsx` which combines the back and submit buttons.
 */
export default FormButtonsFC
